import type { MarketplaceProfileAssetRef } from '@foodfluence/shared';
import type { LocalCreatorsProfileImage, TopContentSlotEntry } from '../types';

type LocalMediaEntry = {
  id: string;
  storagePath: string;
  url: string;
  contentType?: string;
  isVideo?: boolean;
  associatedBrand?: string;
  caption?: string;
};

function localMediaToAssetRef(entry: LocalMediaEntry): MarketplaceProfileAssetRef {
  const ref: MarketplaceProfileAssetRef = {
    id: entry.id,
    type: entry.isVideo ? 'video' : 'photo',
    storagePath: entry.storagePath,
    downloadUrl: entry.url.startsWith('blob:') ? undefined : entry.url,
    contentType: entry.contentType,
  };
  if (entry.associatedBrand) {
    ref.associatedBrand = entry.associatedBrand;
  }
  if (entry.caption) {
    ref.caption = entry.caption;
  }
  return ref;
}

export function profileImageToAssetRef(img: LocalCreatorsProfileImage): MarketplaceProfileAssetRef {
  return localMediaToAssetRef({ ...img, isVideo: false });
}

export function topSlotsToAssets(slots: (TopContentSlotEntry | null)[]): MarketplaceProfileAssetRef[] {
  return slots
    .filter((x): x is TopContentSlotEntry => x !== null && x.storagePath !== '')
    .map((s) => localMediaToAssetRef(s));
}

/** Apply or replace a top-content slot and grow the row to 4–5 when the first 3/4 are all filled. */
export function applyTopSlotMedia(
  prev: (TopContentSlotEntry | null)[],
  index: number,
  entry: TopContentSlotEntry
): (TopContentSlotEntry | null)[] {
  if (index < 0 || index >= prev.length) {
    return prev;
  }
  const next = [...prev];
  const prevSlot = next[index];
  if (prevSlot?.url?.startsWith('blob:')) {
    URL.revokeObjectURL(prevSlot.url);
  }
  next[index] = entry;
  const allFilled = (n: (TopContentSlotEntry | null)[]) => n.length > 0 && n.every((s) => Boolean(s));
  if (allFilled(next) && next.length === 3) {
    return [...next, null];
  }
  if (allFilled(next) && next.length === 4) {
    return [...next, null];
  }
  return next;
}

export function padTopSlotsFromResolved(
  resolved: TopContentSlotEntry[]
): (TopContentSlotEntry | null)[] {
  const n = resolved.length;
  if (n === 0) return [null, null, null];
  if (n === 1) return [resolved[0], null, null];
  if (n === 2) return [resolved[0], resolved[1], null];
  if (n === 3) return [resolved[0], resolved[1], resolved[2], null];
  if (n === 4) return [resolved[0], resolved[1], resolved[2], resolved[3], null];
  return [resolved[0], resolved[1], resolved[2], resolved[3], resolved[4]];
}

/** Move a filled slot to another index, then compact filled items and re-pad trailing empties. */
export function reorderTopSlots(
  slots: (TopContentSlotEntry | null)[],
  fromIndex: number,
  toIndex: number
): (TopContentSlotEntry | null)[] {
  const fromEntry = slots[fromIndex];
  if (!fromEntry || fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return slots;
  }
  if (fromIndex >= slots.length || toIndex >= slots.length) {
    return slots;
  }
  const next = [...slots];
  next.splice(fromIndex, 1);
  next.splice(toIndex, 0, fromEntry);
  const filled = next.filter((s): s is TopContentSlotEntry => s !== null);
  return padTopSlotsFromResolved(filled);
}
